/**
 * HeaderMoreMenu · iter169 (2026-02-27)
 *
 * 3-Punkte-Menü im Header. Kontext-sensitiv:
 *  - /marketplace*       → News/FAQ für Bereich MARKETPLACE
 *  - /stilltouchedx*     → News/FAQ für Bereich STILLTOUCHEDX
 *  - alles andere        → News/FAQ für Bereich SOCIAL
 * Impressum · AGB · Datenschutz sind global identisch.
 *
 * News-Overlay: Feed-Style (Post-Cards), Desktop überlagert mittlere Feed-Spalte.
 * FAQ-Overlay:  Feed-Style (Post-Cards).
 * Statische Seiten: reiner Prose-Text.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MoreVertical, Share2, Pencil, Shield, Layers, Clapperboard, LogOut, UserCheck, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { ContentOverlay, StaticPageContent } from "@/components/common/ContentOverlay";
import FeedStyleArticles from "@/components/common/FeedStyleArticles";

const STATIC_KINDS = [
  { key: "IMPRESSUM", label: "Impressum" },
  { key: "AGB", label: "AGB" },
  { key: "PRIVACY", label: "Datenschutz" },
];

const PAGE_SIZE = 20;

function detectArea(pathname) {
  if (pathname.startsWith("/marketplace") || /^\/user\/[^/]+\/marketplace/.test(pathname)) return "MARKETPLACE";
  if (pathname.startsWith("/stilltouchedx") || pathname.startsWith("/stx")
      || /^\/user\/[^/]+\/(stilltouchedx|stx)/.test(pathname)) return "STILLTOUCHEDX";
  return "SOCIAL";
}

function areaLabelText(area) {
  return { MARKETPLACE: "Marketplace", STILLTOUCHEDX: "stilltouchedX", SOCIAL: "Social" }[area] || area;
}

export default function HeaderMoreMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAuthed = user && user !== false;
  const area = detectArea(location.pathname);
  const [open, setOpen] = useState(false);
  const [overlay, setOverlay] = useState(null); // {type,key,title,style}
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [followReqCount, setFollowReqCount] = useState(0);
  const menuRef = useRef(null);

  // iter169b · Ungelesen-Dot: vergleicht latest_updated_at (Server) gegen localStorage.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/content-updates");
        if (!mounted) return;
        const latest = data.latest_updated_at;
        if (!latest) { setHasUnread(false); return; }
        const seen = localStorage.getItem("moreMenuLastSeen") || "";
        setHasUnread(latest > seen);
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, [location.pathname]);

  const markSeen = () => {
    localStorage.setItem("moreMenuLastSeen", new Date().toISOString());
    setHasUnread(false);
  };

  // Follow-Anfragen-Zähler (Phase 2) für Badge im Account-Menü.
  useEffect(() => {
    if (!isAuthed) { setFollowReqCount(0); return; }
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/me/follow-requests/count");
        if (mounted) setFollowReqCount(data.total || 0);
      } catch { /* silent */ }
    };
    load();
    const onRefresh = () => load();
    window.addEventListener("follow-requests-refresh", onRefresh);
    return () => { mounted = false; window.removeEventListener("follow-requests-refresh", onRefresh); };
  }, [isAuthed, location.pathname]);

  useEffect(() => {
    const onOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // Bei Route-Wechsel schließen
  useEffect(() => { setOverlay(null); setContent(null); setOpen(false); }, [location.pathname]);

  const openArticles = async (kind, label) => {
    setOpen(false);
    setOverlay({ type: "articles", key: kind, title: `${label} · ${areaLabelText(area)}`, style: "feed" });
    setLoading(true);
    try {
      const { data } = await api.get(
        `/articles?area=${area}&kind=${kind}&limit=${PAGE_SIZE}&offset=0`
      );
      setContent({
        items: data.items || [],
        nextOffset: data.has_more ? data.next_offset : null,
        total: data.total || 0,
        kind,
      });
    } catch {
      setContent({ items: [], nextOffset: null, total: 0, kind });
    } finally { setLoading(false); }
  };

  const loadMore = useCallback(async () => {
    if (!content || content.nextOffset == null) return;
    try {
      const { data } = await api.get(
        `/articles?area=${area}&kind=${content.kind}&limit=${PAGE_SIZE}&offset=${content.nextOffset}`
      );
      setContent((prev) => ({
        ...prev,
        items: [...(prev?.items || []), ...(data.items || [])],
        nextOffset: data.has_more ? data.next_offset : null,
        total: data.total ?? prev?.total ?? 0,
      }));
    } catch { /* silent */ }
  }, [area, content]);

  const openStatic = async (kind, label) => {
    setOpen(false);
    setOverlay({ type: "static", key: kind, title: label, style: "prose" });
    setLoading(true);
    try {
      const { data } = await api.get(`/static-pages/${kind}`);
      setContent({ body: data.body || "" });
    } catch {
      setContent({ body: "" });
    } finally { setLoading(false); }
  };

  const closeOverlay = () => { setOverlay(null); setContent(null); };

  // Eigenes Profil teilen — immer nur der Social-Handle (kein STX-Leak).
  const shareOwnProfile = async () => {
    setOpen(false);
    try {
      const { data } = await api.get("/profile");
      if (!data?.handle) return;
      const url = `${window.location.origin}/user/${data.handle}`;
      if (navigator.share) await navigator.share({ url, title: "stilltouched" });
      else { await navigator.clipboard.writeText(url); setShareToast(true); setTimeout(() => setShareToast(false), 2200); }
    } catch { /* abgebrochen */ }
  };

  const goTo = (path) => { setOpen(false); navigate(path); };
  const doLogout = async () => { setOpen(false); await logout(); navigate("/"); };

  const ACCOUNT_ITEMS = [
    { icon: Share2, label: "Profil teilen", onClick: shareOwnProfile, testId: "header-more-share" },
    { icon: Pencil, label: "Profil bearbeiten", onClick: () => goTo("/profile/edit"), testId: "header-more-edit-profile" },
    { icon: UserCheck, label: "Follow-Anfragen", onClick: () => goTo("/profile/follow-requests"), testId: "header-more-follow-requests", badge: followReqCount },
    { icon: Lock, label: "Privatsphäre", onClick: () => goTo("/profile/privacy"), testId: "header-more-privacy-settings" },
    { icon: Shield, label: "Sicherheit & Verifikation", onClick: () => goTo("/profile/security"), testId: "header-more-security" },
    { icon: Layers, label: "Meine Bereiche verwalten", onClick: () => goTo("/profile/access"), testId: "header-more-access" },
    { icon: Clapperboard, label: "Studio", onClick: () => goTo("/studio"), testId: "header-more-studio" },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        data-testid="header-more-btn"
        onClick={() => { setOpen((v) => !v); markSeen(); }}
        aria-label="Mehr"
        className="relative p-2 text-white/70 hover:text-white transition-colors"
      >
        <MoreVertical size={20} />
        {hasUnread && (
          <span
            data-testid="header-more-unread-dot"
            aria-label="Ungelesene Aktualisierungen"
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-[#0A0A0A]"
          />
        )}
        {!hasUnread && followReqCount > 0 && (
          <span
            data-testid="header-more-followreq-dot"
            aria-label="Offene Follow-Anfragen"
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-[#0A0A0A]"
          />
        )}
      </button>
      {open && (
        <div
          data-testid="header-more-menu"
          className="absolute right-0 mt-2 min-w-[220px] bg-white border border-[#E5E5E5] rounded-xl shadow-lg py-1 z-[80]"
        >
          <MenuItem testId="header-more-news" label={`News · ${areaLabelText(area)}`}
            onClick={() => openArticles("NEWS", "News")} />
          <MenuItem testId="header-more-faq" label={`FAQ · ${areaLabelText(area)}`}
            onClick={() => openArticles("FAQ", "FAQ")} />

          {isAuthed && (
            <>
              <div className="my-1 border-t border-[#E5E5E5]" />
              {ACCOUNT_ITEMS.map((it) => (
                <IconMenuItem key={it.testId} icon={it.icon} label={it.label} onClick={it.onClick} testId={it.testId} badge={it.badge} />
              ))}
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-sm text-[#0A0A0A]">Sprache</span>
                <LanguageSwitcher />
              </div>
              <button data-testid="header-more-logout" onClick={doLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors">
                <LogOut size={15} /> Abmelden
              </button>
            </>
          )}

          <div className="my-1 border-t border-[#E5E5E5]" />
          {STATIC_KINDS.map((k) => (
            <MenuItem key={k.key} testId={`header-more-${k.key.toLowerCase()}`} label={k.label}
              onClick={() => openStatic(k.key, k.label)} />
          ))}
        </div>
      )}
      {overlay && (
        <ContentOverlay
          title={overlay.title}
          onClose={closeOverlay}
          testId={`header-overlay-${overlay.key}`}
          variant={overlay.style === "feed" ? "feed" : "prose"}
        >
          {loading ? (
            <div className="text-sm text-[#666] px-4 py-8 text-center">Lade…</div>
          ) : overlay.style === "feed" ? (
            <FeedStyleArticles
              items={content?.items || []}
              area={area}
              hasMore={content?.nextOffset != null}
              onLoadMore={loadMore}
            />
          ) : (
            <StaticPageContent body={content?.body || ""} />
          )}
        </ContentOverlay>
      )}
      {shareToast && (
        <div data-testid="header-share-toast" className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[90] bg-[#0A0A0A] text-white text-xs px-4 py-2 rounded-full shadow-lg">
          Profil-Link kopiert
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="w-full text-left px-3 py-2 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors">
      {label}
    </button>
  );
}

function IconMenuItem({ icon: Icon, label, onClick, testId, badge }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors">
      <Icon size={15} className="text-[#999]" />
      <span className="flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span data-testid={`${testId}-badge`} className="min-w-5 h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}
